import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Compass, Send, Loader2, CheckCircle2, Link2, XCircle, RefreshCw, Sparkles, Plus } from "lucide-react";
import { CcSyncLine } from "@/components/CcSyncLine";
import { useAuth } from "@/context/AuthContext";

const STATUS = {
  discovery_scheduled: { label: "Scheduled", cls: "text-[#EAE04A] border-[#EAE04A]/30 bg-[#EAE04A]/10" },
  notes_sent_for_review: { label: "Notes sent for review", cls: "text-[#00F0FF] border-[#00F0FF]/30 bg-[#00F0FF]/10" },
  awaiting_client_reply: { label: "Awaiting client reply", cls: "text-[#B084FF] border-[#B084FF]/30 bg-[#B084FF]/10" },
  revision_requested: { label: "Revision requested", cls: "text-[#FF9F45] border-[#FF9F45]/30 bg-[#FF9F45]/10" },
  discovery_confirmed: { label: "Confirmed by client", cls: "text-[#00F0FF] border-[#00F0FF]/30 bg-[#00F0FF]/10" },
  discovery_completed: { label: "Completed", cls: "text-[#00FF41] border-[#00FF41]/30 bg-[#00FF41]/10" },
  cancelled: { label: "Cancelled", cls: "text-[#FF5C5C] border-[#FF5C5C]/30 bg-[#FF5C5C]/10" },
};

// Status label that surfaces the revision round so both PSDoc and CC stay in step ("Rev N" / "re-review N").
const statusLabel = (d) => {
  const base = (STATUS[d.status] || {}).label || d.status;
  const r = d.revision_round || 0;
  if (d.status === "revision_requested") return `Revision requested · Rev ${r}`;
  if (d.status === "notes_sent_for_review" && r > 0) return `Notes sent for re-review ${r}`;
  if (d.status === "awaiting_client_reply" && r > 0) return `Awaiting client reply · Rev ${r}`;
  return base;
};

const GREEN_SCOPE = [
  "New site / branch build",
  "New firewall deployment",
  "HA cluster deployment",
  "SD-WAN rollout",
  "New data centre build",
  "Greenfield security stack (NGFW/UTM)",
  "Remote access VPN",
  "Network segmentation / micro-segmentation",
  "Wireless / LAN deployment",
  "Internet edge / DMZ build",
];

const BROWN_SCOPE = [
  "Inter-vendor migration",
  "Intra-vendor migration",
  "Addition of services",
  "PROD / DR failover",
  "Migration of services between DCs",
  "Firmware upgrades",
  "Hardware refresh / replacement",
  "Policy / rule-base cleanup",
  "Bandwidth / circuit upgrade",
  "Topology change / re-architecture",
];

const CLOSED = ["discovery_completed", "cancelled"];
const EDITABLE = ["discovery_scheduled", "revision_requested"];

function fmt(s) { try { return new Date(s).toLocaleString("en-AU", { timeZone: "Australia/Sydney", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZoneName: "short" }); } catch { return "—"; } }
function slug(s) { return s.replace(/[^a-z0-9]/gi, "-").toLowerCase(); }

export default function Discovery() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState("active");
  const [busy, setBusy] = useState(false);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [picked, setPicked] = useState({});
  const [f, setF] = useState({
    notes: "", clarifying_questions: "", bench_type: "", green_scope: [], brown_scope: [],
    technical_requirements: "", network_topology: "", number_of_sites: "", preferred_vendor: "", features_required: "",
  });

  const load = async () => {
    try { const { data } = await api.get("/discoveries"); setItems(data); }
    catch { toast.error("Failed to load discovery jobs"); }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const open = (d) => {
    setSel(d);
    setSuggestions([]); setPicked({});
    setF({
      notes: d.notes || "", clarifying_questions: d.clarifying_questions || "",
      bench_type: d.bench_type || "", green_scope: d.green_scope || [], brown_scope: d.brown_scope || [],
      technical_requirements: d.technical_requirements || "", network_topology: d.network_topology || "",
      number_of_sites: d.number_of_sites || "", preferred_vendor: d.preferred_vendor || "",
      features_required: d.features_required || "",
    });
  };

  const set = (k, v) => setF((cur) => ({ ...cur, [k]: v }));
  const setBench = (b) => setF((cur) => ({ ...cur, bench_type: b,
    green_scope: b === "green" ? cur.green_scope : [], brown_scope: b === "brown" ? cur.brown_scope : [] }));
  const toggle = (key, s) => setF((cur) => ({ ...cur, [key]: cur[key].includes(s) ? cur[key].filter((x) => x !== s) : [...cur[key], s] }));

  const submit = async () => {
    if (!f.bench_type) { toast.error("Select a bench type (Green or Brown) first", { duration: 3000 }); return; }
    const activeScope = f.bench_type === "green" ? f.green_scope : f.brown_scope;
    if (activeScope.length === 0) { toast.error(`Select at least one ${f.bench_type}-bench work type`, { duration: 3000 }); return; }
    if (!f.notes.trim()) { toast.error("Enter your discovery notes first", { duration: 3000 }); return; }
    setBusy(true);
    try {
      await api.put(`/discoveries/${sel.id}/submit`, f);
      toast.success("Notes submitted for client review");
      setSel(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  const suggestQuestions = async () => {
    setSuggestBusy(true);
    try {
      const { data } = await api.post(`/discoveries/${sel.id}/suggest-questions`, f);
      setSuggestions(data.questions || []);
      setPicked({});
      if (!data.questions?.length) toast.info("No questions came back — try adding a few notes first.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate questions"); }
    finally { setSuggestBusy(false); }
  };

  const addPicked = () => {
    const chosen = suggestions.filter((_, i) => picked[i]).map((q) => q.question.trim());
    if (chosen.length === 0) { toast.error("Tick at least one question to add"); return; }
    const existing = (f.clarifying_questions || "").split("\n").map((x) => x.trim()).filter(Boolean);
    const merged = [...existing];
    chosen.forEach((q) => { if (!merged.includes(q)) merged.push(q); });
    set("clarifying_questions", merged.join("\n"));
    toast.success(`Added ${chosen.length} question${chosen.length > 1 ? "s" : ""}`);
    setPicked({});
  };

  const terminal = sel && CLOSED.includes(sel.status);
  const overrideActive = sel && isAdmin && !terminal && !EDITABLE.includes(sel.status);
  const locked = sel && (terminal || (!EDITABLE.includes(sel.status) && !isAdmin));
  const cancelled = sel && sel.status === "cancelled";

  const filtered = items.filter((d) => {
    if (filter === "all") return true;
    if (filter === "active") return !CLOSED.includes(d.status);
    if (filter === "cancelled") return d.status === "cancelled";
    if (filter === "completed") return d.status === "discovery_completed";
    return true;
  });
  const counts = {
    active: items.filter((d) => !CLOSED.includes(d.status)).length,
    cancelled: items.filter((d) => d.status === "cancelled").length,
    completed: items.filter((d) => d.status === "discovery_completed").length,
  };

  const workOptions = f.bench_type === "green" ? GREEN_SCOPE : f.bench_type === "brown" ? BROWN_SCOPE : [];
  const workKey = f.bench_type === "green" ? "green_scope" : "brown_scope";

  return (
    <div className="p-8 lg:p-10">
      <div className="mb-8 border-b border-white/10 pb-6">
        <div className="text-xs tracking-[0.2em] uppercase text-[#484F58] mb-2 font-mono-jb">Pre-Sales</div>
        <h1 className="font-mono-jb text-4xl sm:text-5xl font-extrabold tracking-tighter text-white flex items-center gap-3">
          <Compass className="text-[#00F0FF]" size={36} /> Discovery<span className="text-[#00F0FF] cursor-blink">_</span>
        </h1>
        <p className="text-[#8B949E] text-sm mt-3 max-w-2xl leading-relaxed">
          Discovery jobs arrive from <span className="text-[#B084FF]">Command Centre</span>. Pick the engagement type and work, capture the technical detail, then submit for client review. Cancellations are driven from Command Centre.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="flex gap-1 mb-3 font-mono-jb text-[10px] uppercase tracking-wider" data-testid="discovery-filter">
            {[["active", `Active (${counts.active})`], ["cancelled", `Cancelled (${counts.cancelled})`], ["completed", `Done (${counts.completed})`], ["all", "All"]].map(([k, lbl]) => (
              <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)}
                className={`px-2 py-1 border rounded-sm transition-colors ${filter === k ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 text-[#8B949E] hover:border-white/25"}`}>{lbl}</button>
            ))}
          </div>
          <div className="space-y-3" data-testid="discovery-list">
            {filtered.length === 0 && <div className="text-[#484F58] font-mono-jb text-sm">No discovery jobs in this view.</div>}
            {filtered.map((d) => (
              <div key={d.id} role="button" tabIndex={0} data-testid={`discovery-item-${d.id}`} onClick={() => open(d)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") open(d); }}
                className={`w-full text-left border p-4 transition-colors duration-200 cursor-pointer ${sel?.id === d.id ? "border-[#00F0FF]/50 bg-[#00F0FF]/5" : "border-white/10 bg-[#0B0C10] hover:border-white/25"} ${d.status === "cancelled" ? "opacity-70" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono-jb text-sm text-white truncate">{d.client_name || d.opportunity_id}</span>
                  <span data-testid={`discovery-status-${d.id}`} className={`text-[9px] uppercase tracking-wider font-mono-jb px-1.5 py-0.5 rounded-sm border ${(STATUS[d.status] || {}).cls}`}>{statusLabel(d)}</span>
                </div>
                <div className="text-[10px] text-[#8B949E] font-mono-jb mt-1 flex items-center gap-1"><Link2 size={10} /> {d.opportunity_id}</div>
                {d.bench_type && <div className="text-[10px] font-mono-jb mt-0.5"><span className={d.bench_type === "brown" ? "text-[#EAE04A]" : "text-[#00FF41]"}>{d.bench_type === "brown" ? "Brown bench" : "Green bench"}</span></div>}
                <div className="text-[10px] text-[#484F58] font-mono-jb mt-0.5">Updated {fmt(d.updated_at)}</div>
                {d.status !== "cancelled" && (
                  <div className="mt-2 pt-2 border-t border-white/10">
                    <CcSyncLine sync={d.cc_sync} opportunityId={d.opportunity_id} onRetried={load} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          {!sel ? (
            <div className="border border-white/10 bg-[#0B0C10] p-10 text-center text-[#484F58] font-mono-jb text-sm">Select a discovery job to add your notes.</div>
          ) : (
            <div className="border border-white/10 bg-[#0B0C10] p-6" data-testid="discovery-detail">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-mono-jb text-xl font-bold text-white">{sel.client_name || sel.opportunity_id}</h2>
                <span data-testid="discovery-detail-status" className={`text-[9px] uppercase tracking-wider font-mono-jb px-2 py-1 rounded-sm border ${(STATUS[sel.status] || {}).cls}`}>{statusLabel(sel)}</span>
              </div>
              {sel.status === "discovery_completed" && (
                <div className="flex items-center gap-2 text-[#00FF41] font-mono-jb text-xs mb-4"><CheckCircle2 size={14} /> Completed — Pre-Sales project created.</div>
              )}
              {cancelled && (
                <div className="flex items-start gap-2 text-[#FF5C5C] font-mono-jb text-xs mb-4 border border-[#FF5C5C]/25 bg-[#FF5C5C]/5 p-3" data-testid="discovery-cancelled-banner">
                  <XCircle size={14} className="mt-0.5 shrink-0" />
                  <span>Cancelled in Command Centre{sel.status_actor ? ` by ${sel.status_actor}` : ""}{sel.status_reason ? ` — reason: ${sel.status_reason}` : ""}. This job is read-only.</span>
                </div>
              )}
              {sel.status === "awaiting_client_reply" && (
                <div className="flex items-center gap-2 text-[#B084FF] font-mono-jb text-xs mb-4" data-testid="discovery-awaiting-banner">
                  <Send size={14} /> {(sel.revision_round || 0) > 0 ? `Revision ${sel.revision_round} sent — ` : ""}Command Centre has emailed the intake form to the client — awaiting their reply{sel.status_actor ? ` (sent by ${sel.status_actor})` : ""}.
                </div>
              )}
              {sel.status === "revision_requested" && (
                <div className="flex items-start gap-2 text-[#FF9F45] font-mono-jb text-xs mb-4 border border-[#FF9F45]/25 bg-[#FF9F45]/5 p-3" data-testid="discovery-revision-banner">
                  <RefreshCw size={14} className="mt-0.5 shrink-0" />
                  <span>Client requested changes (Rev {sel.revision_round || 1}){sel.status_actor ? ` via ${sel.status_actor}` : ""}{sel.client_feedback ? `: “${sel.client_feedback}”` : ""}. Revise the detail below and re-submit.</span>
                </div>
              )}
              {Array.isArray(sel.client_answers) && sel.client_answers.length > 0 && (
                <div className="mb-4 border border-[#00FF41]/25 bg-[#00FF41]/5 p-3" data-testid="client-answers-card">
                  <div className="flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase font-mono-jb text-[#00FF41] mb-2">
                    <CheckCircle2 size={12} /> Client answers received ({sel.client_answers.length})
                  </div>
                  <div className="space-y-2">
                    {sel.client_answers.map((a, i) => (
                      <div key={i} className="font-mono-jb text-xs" data-testid={`client-answer-${i}`}>
                        <div className="text-[#8B949E]">{a.question}</div>
                        <div className="text-[#C9D1D9]">↳ {a.answer}</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] text-[#484F58] mt-2 font-mono-jb">These auto-resolve matching config-readiness questions when you click "Suggest questions".</div>
                </div>
              )}
              {locked && !terminal && (
                <div className="flex items-start gap-2 text-[#8B949E] font-mono-jb text-xs mb-4 border border-white/10 bg-white/[0.02] p-3" data-testid="discovery-locked-banner">
                  <Send size={14} className="mt-0.5 shrink-0" />
                  <span>Notes already sent for review — this discovery is read-only. It can be edited again if the client requests changes, or an admin overrides.</span>
                </div>
              )}
              {overrideActive && (
                <div className="flex items-start gap-2 text-[#EAE04A] font-mono-jb text-xs mb-4 border border-[#EAE04A]/25 bg-[#EAE04A]/5 p-3" data-testid="discovery-override-banner">
                  <RefreshCw size={14} className="mt-0.5 shrink-0" />
                  <span>Admin override — this discovery is already “{(STATUS[sel.status] || {}).label || sel.status}”. Re-submitting will overwrite the notes and re-notify Command Centre.</span>
                </div>
              )}

              {/* Engagement type */}
              <div className="mb-5">
                {locked && !f.bench_type ? (
                  <div className="font-mono-jb text-xs text-[#8B949E]">Engagement type: <span className="text-[#484F58]">not recorded</span></div>
                ) : (
                  <>
                    <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Engagement type {!locked && <span className="text-[#00F0FF]">*</span>}</label>
                    {!locked && <p className="text-[10px] text-[#484F58] font-mono-jb mt-1 mb-2">PSDoc leads this call — pick before capturing detail.</p>}
                    <div className="flex gap-2">
                      <button type="button" data-testid="bench-green" disabled={locked} onClick={() => setBench("green")}
                        className={`flex-1 border p-3 text-left transition-colors duration-150 ${f.bench_type === "green" ? "border-[#00FF41] bg-[#00FF41]/10" : "border-white/15 bg-[#07080B] hover:border-white/30"} ${locked ? "opacity-60" : ""}`}>
                        <div className="font-mono-jb text-sm font-bold text-white">Green bench</div>
                        <div className="text-[10px] text-[#8B949E] font-mono-jb mt-0.5">New build / greenfield deployment</div>
                      </button>
                      <button type="button" data-testid="bench-brown" disabled={locked} onClick={() => setBench("brown")}
                        className={`flex-1 border p-3 text-left transition-colors duration-150 ${f.bench_type === "brown" ? "border-[#EAE04A] bg-[#EAE04A]/10" : "border-white/15 bg-[#07080B] hover:border-white/30"} ${locked ? "opacity-60" : ""}`}>
                        <div className="font-mono-jb text-sm font-bold text-white">Brown bench</div>
                        <div className="text-[10px] text-[#8B949E] font-mono-jb mt-0.5">Change to an existing estate</div>
                      </button>
                    </div>
                    {workOptions.length > 0 && (
                      <div className={`mt-3 border p-3 ${f.bench_type === "brown" ? "border-[#EAE04A]/20 bg-[#EAE04A]/5" : "border-[#00FF41]/20 bg-[#00FF41]/5"}`} data-testid="work-scope">
                        <div className={`text-[10px] tracking-[0.15em] uppercase font-mono-jb mb-2 ${f.bench_type === "brown" ? "text-[#EAE04A]" : "text-[#00FF41]"}`}>
                          {f.bench_type === "brown" ? "Brown-bench" : "Green-bench"} work type (select all that apply)
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {workOptions.map((s) => (
                            <label key={s} data-testid={`work-${slug(s)}`}
                              className={`flex items-center gap-2 font-mono-jb text-xs cursor-pointer ${locked ? "opacity-60 pointer-events-none" : ""}`}>
                              <input type="checkbox" checked={f[workKey].includes(s)} onChange={() => toggle(workKey, s)}
                                className={f.bench_type === "brown" ? "accent-[#EAE04A]" : "accent-[#00FF41]"} disabled={locked} />
                              <span className="text-[#C9D1D9]">{s}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Structured technical detail */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div className="sm:col-span-2">
                  <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Technical requirements</label>
                  <Textarea data-testid="disc-tech-req" value={f.technical_requirements} onChange={(e) => set("technical_requirements", e.target.value)} disabled={locked} rows={4}
                    placeholder="Core technical requirements captured during discovery..." className="mt-2 bg-[#07080B] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Network topology</label>
                  <Textarea data-testid="disc-topology" value={f.network_topology} onChange={(e) => set("network_topology", e.target.value)} disabled={locked} rows={3}
                    placeholder="HQ + branches, WAN/internet links, core, DC layout..." className="mt-2 bg-[#07080B] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Number of sites</label>
                  <Input data-testid="disc-sites" value={f.number_of_sites} onChange={(e) => set("number_of_sites", e.target.value)} disabled={locked}
                    placeholder="e.g. 1 HQ + 3 branches" className="mt-2 bg-[#07080B] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Preferred vendor</label>
                  <Input data-testid="disc-vendor" value={f.preferred_vendor} onChange={(e) => set("preferred_vendor", e.target.value)} disabled={locked}
                    placeholder="e.g. Fortinet" className="mt-2 bg-[#07080B] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Features required</label>
                  <Textarea data-testid="disc-features" value={f.features_required} onChange={(e) => set("features_required", e.target.value)} disabled={locked} rows={3}
                    placeholder="SD-WAN, SSL inspection, IPS, ZTNA, logging/retention..." className="mt-2 bg-[#07080B] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
                </div>
              </div>

              {/* Raw notes + clarifying questions */}
              <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Raw discovery notes <span className="text-[#00F0FF]">*</span></label>
              <Textarea data-testid="discovery-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} disabled={locked}
                placeholder="Paste the raw notes you took during the discovery meeting..." rows={8}
                className="mt-2 bg-[#07080B] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
              <div className="flex items-center justify-between mt-4">
                <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Clarifying questions for the client (optional)</label>
                {!locked && (
                  <Button type="button" data-testid="suggest-questions-btn" onClick={suggestQuestions} disabled={suggestBusy}
                    variant="outline" className="h-7 rounded-sm border-[#B084FF]/40 bg-[#B084FF]/10 text-[#B084FF] hover:bg-[#B084FF]/20 hover:text-[#B084FF] font-mono-jb text-[10px] uppercase tracking-widest">
                    {suggestBusy ? <Loader2 className="animate-spin mr-1" size={12} /> : <Sparkles size={12} className="mr-1" />} Suggest questions
                  </Button>
                )}
              </div>
              {suggestions.length > 0 && !locked && (
                <div className="mt-2 border border-[#B084FF]/25 bg-[#B084FF]/5 p-3" data-testid="suggestions-panel">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] tracking-[0.15em] uppercase font-mono-jb text-[#B084FF]">
                      Config-readiness questions — tick to add
                    </div>
                    <Button type="button" data-testid="add-selected-btn" onClick={addPicked}
                      className="h-6 rounded-sm bg-[#B084FF] text-black hover:bg-[#B084FF] font-mono-jb text-[10px] uppercase tracking-widest font-bold px-2">
                      <Plus size={11} className="mr-0.5" /> Add selected
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    {suggestions.map((q, i) => (
                      <label key={i} data-testid={`suggestion-${i}`}
                        className="flex items-start gap-2 font-mono-jb text-xs cursor-pointer border border-white/5 bg-[#07080B] p-2 hover:border-white/15 transition-colors">
                        <input type="checkbox" checked={!!picked[i]} onChange={() => setPicked((p) => ({ ...p, [i]: !p[i] }))}
                          className="accent-[#B084FF] mt-0.5" data-testid={`suggestion-check-${i}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-[#00F0FF]/30 bg-[#00F0FF]/10 text-[#00F0FF]">{q.domain}</span>
                            {q.answered
                              ? <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-[#00FF41]/30 bg-[#00FF41]/10 text-[#00FF41]">Likely answered</span>
                              : <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-[#EAE04A]/30 bg-[#EAE04A]/10 text-[#EAE04A]">Open gap</span>}
                          </div>
                          <div className="text-[#C9D1D9] leading-snug">{q.question}</div>
                          {q.note && <div className="text-[10px] text-[#484F58] mt-0.5">{q.note}</div>}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <Textarea data-testid="discovery-questions" value={f.clarifying_questions} onChange={(e) => set("clarifying_questions", e.target.value)} disabled={locked}
                placeholder="Anything you need the client to confirm or expand on (one per line)..." rows={4}
                className="mt-2 bg-[#07080B] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
              {!locked && (
                <Button data-testid="discovery-submit" onClick={submit} disabled={busy}
                  className="mt-5 rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF]">
                  {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <Send size={14} className="mr-1" />} {overrideActive ? "Override & re-submit" : sel.status === "revision_requested" ? "Re-submit revised notes" : "Submit for client review"}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
